import { PartTypes } from '@parallel-fluidics/shared/constants';
import { projectFromFirestore } from '@parallel-fluidics/shared/models/projects';
import { comparePPartsDesc, pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';

/**
 * Fetch every piston part across all projects, each paired with its owning
 * project's name so callers can build the full pPartId (e.g. "P072-0001-A-PN12-1").
 *
 * Pistons mostly live in a shared internal "Pistons" project but may be
 * created under any project, hence the collectionGroup query rather than a
 * single project's subcollection. Requires collection-group scope on the
 * `parts.type` single-field index (fieldOverrides in
 * apps/functions/firestore.indexes.json). Sorted with the shared part
 * comparator, so the picker orders like the other part pickers.
 *
 * Parts that fail validation are skipped, as are pistons whose owning project
 * can't be resolved (deleted or invalid): without the project prefix the
 * full id stored on process logs would be ambiguous across projects.
 *
 * @returns {Promise<Array<{
 *   piston: import('@parallel-fluidics/shared/models/projects/parts').Piston,
 *   projectDocId: string,
 *   projectName: string,
 * }>>}
 */
export default async function fetchPistonParts() {
  const db = getFirestore();
  const snap = await getDocs(query(
    collectionGroup(db, 'parts'),
    where('type', '==', PartTypes.PISTON),
  ));

  const rows = snap.docs.reduce((acc, partDoc) => {
    const piston = pPartFromFirestore(partDoc);
    if (piston.validation === 'failed') return acc;
    // Parts live at projects/{projectDocId}/parts/{id}.
    acc.push({ piston, projectDocId: partDoc.ref.parent.parent.id });
    return acc;
  }, []).sort((a, b) => comparePPartsDesc(a.piston, b.piston));

  const projectDocIds = [...new Set(rows.map((row) => row.projectDocId))];
  const projectNames = Object.fromEntries(await Promise.all(
    projectDocIds.map(async (projectDocId) => {
      const projectDoc = await getDoc(doc(db, 'projects', projectDocId));
      const project = projectDoc.exists() ? projectFromFirestore(projectDoc) : null;
      return [projectDocId, project?.validation === 'failed' ? '' : (project?.name ?? '')];
    }),
  ));

  return rows
    .map((row) => ({ ...row, projectName: projectNames[row.projectDocId] }))
    .filter((row) => row.projectName);
}
